import { Board, QuoteState } from "../../../../reducers/uploadedBoard";
import {
  StatusContainer,
  StepsContainer,
  SummaryContainer,
} from "../../../internal/BoardInventory/BoardInventoryStyles";
import StatusSummaryTable from "../../../internal/BoardInventory/StatusSummaryTable";
import ManufacturingStatus from "../ManufacturingStatus";
import { FakeLink, InputLabel } from "../../../../constants/Styles";
import {
  CustomerSpecifications,
  ManufacturingStatusContainer,
  NicknameContainer,
  SpecificationsIcons,
  SpecificationsNotes,
} from "../DashboardStyles";
import React, { useContext, useState } from "react";
import ShareBoard from "../../../ShareBoard";
import {
  BoardCard,
  BoardInfo,
  BoardNameLink,
  BoardNickname,
  BoardPreview,
  BoardPreviewContainer,
  BoardStatePill,
  InfoRows,
  NameLinkAndTools,
  ProjectOwnerRow,
  TitleInfoRow,
} from "../../../boardViews/BoardCard/BoardCardStyles";
import getBoardLink from "../../../../utils/links/boards";
import Icon from "../../../common/Icon";
import { useAppSelector } from "../../../../utils/hooks/store";
import { useBoardFromContext } from "../../../../utils/hooks/commonly-stored/uploaded-board/board";
import LoadingIcon from "../../../common/LoadingIcon";
import Link from "next/link";
import ProjectOwner from "../../../ShareBoard/ProjectOwner";
import { DetailsInfoRow } from "../../../Orders/OrdersStyles";
import {
  dateStringToHumanReadableDate,
  datetimeStringToHumanReadableDate,
  formatCurrency,
} from "../../../../utils/valueFormatting";
import {
  BoardIdContext,
  BoardShipmentIds,
  useLoadBoardForReviewBoardFlow,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import ShipmentTools from "../../../ReviewBoardFlow/Quote/Shipments/ShipmentTools";
import ModalContentWithCloseButton from "../../../common/ModalContentWithCloseButton";
import Modal from "../../../common/Modal";
import UpdateBoardNickName from "./UpdateBoardNickName";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../../../utils/group";
import useShipmentsFromContext from "../../../../utils/hooks/commonly-stored/uploaded-board/shipments";
import useDownloadTraveler from "../../../../utils/hooks/commonly-stored/uploaded-board/travelerSticker";
import FlashPCBTooltip from "../../../common/FlashPCBTooltip";
import { LithiumPartsWarning } from "../../../internal/BoardFiles/PanelDetails";

export function getBoardStateForDisplay(board: Board): string {
  if (board.quote.state == QuoteState.ORDER_RECEIVED) {
    return board.order_summary?.state || board.quote.state;
  } else if (board.quote.state == QuoteState.QUOTED) {
    if (board.quote.requested_custom_quote) {
      if (board.quote.total === null) {
        return "Awaiting custom quote";
      } else {
        return "Received custom quote";
      }
    }
  }
  return board.quote.state;
}

export default function ShipmentCard() {
  const { boardId, shipmentId } = useContext(
    BoardIdContext,
  ) as BoardShipmentIds;
  useLoadBoardForReviewBoardFlow();
  const { userId, groups } = useAppSelector((state) => state.auth);
  const [shareBoardId, setShareBoardId] = useState<string | null>(null);
  const board = useBoardFromContext().board;
  const [adjustNickname, setAdjustNickname] = useState<boolean>(false);
  const isAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);
  const downloadTravelerSticker = useDownloadTraveler();
  const shipments = useShipmentsFromContext(boardId);
  const shipment = shipments?.find((shipment) => shipment.id == shipmentId);

  if (board === null || shipments === null || shipment === undefined) {
    return (
      <BoardCard>
        <LoadingIcon text={"Loading shipment..."} />
      </BoardCard>
    );
  }

  const boardStateToDisplay = getBoardStateForDisplay(board);
  const closeModal = () => {
    setAdjustNickname(false);
  };

  const boardHasCustomerSpecifications = (board: Board): boolean => {
    return (
      board.manufacturing_details.customer_specifications.cleaning ||
      board.manufacturing_details.customer_specifications.conformal_coating ||
      board.manufacturing_details.customer_specifications.xray_inspection ||
      board.manufacturing_details.customer_specifications.notes !== ""
    );
  };
  return (
    <>
      <ShareBoard
        shareBoardId={shareBoardId}
        setShareBoardId={setShareBoardId}
      />
      <BoardCard>
        <BoardPreviewContainer>
          {board.top_preview !== null && (
            <BoardPreview>
              {/* Don't need to worry about SEO here so disable lint */}
              {/* eslint-disable-next-line */}
              <img
                src={board.top_preview}
                alt={`Board ${board.project_name} preview`}
              />
            </BoardPreview>
          )}
          {board.bottom_preview !== null && (
            <BoardPreview>
              {/* Don't need to worry about SEO here so disable lint */}
              {/* eslint-disable-next-line */}
              <img
                src={board.bottom_preview}
                alt={`Board ${board.project_name} preview`}
              />
            </BoardPreview>
          )}
        </BoardPreviewContainer>
        <BoardInfo>
          <InfoRows>
            <TitleInfoRow>
              <NameLinkAndTools>
                <BoardNameLink
                  href={getBoardLink(board.id, true, shipment.id)}
                  passHref={true}
                  target={"_blank"}
                >
                  {board.project_name}
                </BoardNameLink>
                {isAdminOrEngineer && board.user_id && (
                  <NicknameContainer>
                    <BoardNickname>{board.internal_nickname}</BoardNickname>
                    <FakeLink onClick={() => setAdjustNickname(true)}>
                      {"Update board nickname"}
                    </FakeLink>
                    {board.order_summary !== null && (
                      <FakeLink
                        onClick={() => void downloadTravelerSticker(board.id)}
                      >
                        Traveler
                      </FakeLink>
                    )}
                  </NicknameContainer>
                )}
              </NameLinkAndTools>

              <BoardStatePill>
                <Icon
                  src={"/icons/dollar_card.svg"}
                  alt={boardStateToDisplay}
                  iconDimension={16}
                />
                {boardStateToDisplay}
              </BoardStatePill>
            </TitleInfoRow>
            {board.user_id != userId && (
              <ProjectOwnerRow>
                <ProjectOwner boardId={board.id} userId={board.user_id} />
              </ProjectOwnerRow>
            )}
            <DetailsInfoRow>
              <div>Id: {board.id}</div>
              <div>
                {`Created ${datetimeStringToHumanReadableDate(
                  board.datetime_uploaded,
                )}`}
              </div>
              {board.physical_details !== null && (
                <>
                  <div>
                    {board.physical_details.bounding_box_width.toFixed(2)} x{" "}
                    {board.physical_details.bounding_box_height.toFixed(2)}{" "}
                    {board.physical_details.bounding_box_unit}
                  </div>
                  {board.physical_details.num_layers && (
                    <div>{`Layers: ${board.physical_details.num_layers}`}</div>
                  )}
                </>
              )}
              <div>
                Shipment:{" "}
                {(
                  shipments.findIndex((shipment) => {
                    return shipment.id == shipmentId;
                  }) + 1
                ).toLocaleString()}{" "}
                / {shipments.length.toLocaleString()}
              </div>
              {board.quote.total !== null && (
                <div>
                  Subtotal:{" "}
                  {formatCurrency(board.quote.total, board.quote.currency)}{" "}
                  {board.quote.expires_at !== null &&
                    board.quote.state !== QuoteState.ORDER_RECEIVED &&
                    `(this quotes expires on ${dateStringToHumanReadableDate(
                      board.quote.expires_at,
                    )})`}
                </div>
              )}
              {board.quote.estimated_total !== null && (
                <div>
                  Estimated subtotal:{" "}
                  {formatCurrency(
                    board.quote.estimated_total,
                    board.quote.currency,
                  )}
                </div>
              )}
              {board.order_summary !== null && (
                <div>
                  Order:{" "}
                  <Link
                    href={`/orders/${board.order_summary.id}`}
                    target={"_blank"}
                  >
                    {board.order_summary.id}
                  </Link>
                </div>
              )}
            </DetailsInfoRow>
            <ShipmentTools shipment={shipment} />
            {boardHasCustomerSpecifications(board) && (
              <CustomerSpecifications>
                <InputLabel>Customer specifications:</InputLabel>
                <SpecificationsIcons>
                  {board.manufacturing_details.customer_specifications
                    .cleaning && (
                    <FlashPCBTooltip content={"cleaning requested"}>
                      <Icon
                        src={"/icons/water_drop_white.svg"}
                        alt={"Cleaning"}
                      />
                    </FlashPCBTooltip>
                  )}
                  {board.manufacturing_details.customer_specifications
                    .conformal_coating && (
                    <FlashPCBTooltip content={"conformal coating requested"}>
                      <Icon
                        src={"/icons/spray_can_white.svg"}
                        alt={"Coating"}
                      />
                    </FlashPCBTooltip>
                  )}
                  {board.manufacturing_details.customer_specifications
                    .xray_inspection && (
                    <FlashPCBTooltip content={"x-ray inspection requested"}>
                      <Icon src={"/icons/radiation_white.svg"} alt={"Xray"} />
                    </FlashPCBTooltip>
                  )}
                  {board.manufacturing_details.customer_specifications.notes !==
                    "" && (
                    <Icon src={"/icons/attention_white.svg"} alt={"Special"} />
                  )}
                </SpecificationsIcons>
                <SpecificationsNotes>
                  {board.manufacturing_details.customer_specifications
                    .notes && (
                    <>
                      <InputLabel>Notes:</InputLabel>
                      <>
                        {
                          board.manufacturing_details.customer_specifications
                            .notes
                        }
                      </>
                    </>
                  )}
                </SpecificationsNotes>
              </CustomerSpecifications>
            )}
            <LithiumPartsWarning shipmentParts={shipment.shipment_parts} />
            <ManufacturingStatusContainer>
              <StatusContainer>
                <SummaryContainer>
                  <StatusSummaryTable />
                </SummaryContainer>
                <StepsContainer>
                  <ManufacturingStatus />
                </StepsContainer>
              </StatusContainer>
            </ManufacturingStatusContainer>
          </InfoRows>
        </BoardInfo>
      </BoardCard>
      <Modal isOpen={adjustNickname} onRequestClose={closeModal}>
        <ModalContentWithCloseButton closeModal={closeModal}>
          <UpdateBoardNickName board={board} closeModal={closeModal} />
        </ModalContentWithCloseButton>
      </Modal>
    </>
  );
}
